import { Badge } from "@mantine/core";
import { IconChevronRight, IconThumbDown, IconThumbUp } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import InfoTooltip from "#/components/ui/InfoTooltip";
import { useClient } from "#/hook/useClient";
import { usePrvTranslation } from "#/pages/ClientDetail/components/prv/usePrvTranslation";
import type { LogisticsPrediction, PredictionBoard, ShipmentCard } from "#/types/prediction";
import { formatDate, formatWeekday, isSameLocalDay } from "#/utils/dateTimeUtils";
import {
  categoryColor,
  categoryName,
  lateDays,
  type OutcomeWord,
  outcomeOf,
} from "../predictionUtils";

interface Props {
  card: ShipmentCard;
  board: Pick<PredictionBoard, "categories">;
  now: Date;
}

const OUTCOME_COLOR: Record<OutcomeWord, string> = {
  delivered: "green",
  early: "teal",
  late: "orange",
  not_delivered: "red",
  pending: "gray",
};

/**
 * For a shipment whose day has passed: what happened, stop by stop. The
 * predicted day and load next to the actual delivery day and load, the
 * outcome word, and the feedback the users gave. The numbers are the
 * prediction rows' own outcome fields, written when the next visit was seen.
 */
const ShipmentOutcomeCard = ({ card, board, now }: Props) => {
  const t = usePrvTranslation();
  const { i18n } = useTranslation();
  const { client } = useClient();
  const navigate = useNavigate();
  const name = (productId: number) => categoryName(board, productId);
  const color = (productId: number) => categoryColor(board, productId) ?? "#94a3b8";

  const predictedFor = (prediction: LogisticsPrediction) =>
    isSameLocalDay(prediction.dueFrom, prediction.dueTo)
      ? formatDate(prediction.dueFrom, client)
      : t("predictedDeliveries.card.window", {
          from: formatDate(prediction.dueFrom, client),
          to: formatDate(prediction.dueTo, client),
        });

  const loadList = (lines: { productId: number; quantity: number }[]) => (
    <ul className="flex flex-col gap-0.5">
      {lines.map((line) => (
        <li key={line.productId} className="flex items-center gap-1.5 text-sm text-gray-800">
          <span
            className="inline-block h-2 w-2 shrink-0 rounded-full"
            style={{ backgroundColor: color(line.productId) }}
          />
          <span className="font-semibold tabular-nums">{line.quantity}</span>
          <span>{name(line.productId)}</span>
        </li>
      ))}
    </ul>
  );

  return (
    <section className="flex flex-col gap-3 rounded-lg border border-l-4 border-gray-300 border-l-slate-500 bg-white p-4 shadow-sm">
      <h4 className="flex items-center gap-1.5 text-xs font-semibold tracking-wider text-gray-500 uppercase">
        {t("predictedDeliveries.shipment.happened.title", {
          date: formatDate(card.shipment.shipmentDate, client),
          weekday: formatWeekday(card.shipment.shipmentDate, i18n.language),
        })}
        <InfoTooltip content={t("predictedDeliveries.shipment.happened.tip")} size="sm" />
      </h4>
      <ul className="flex flex-col divide-y divide-gray-100">
        {card.predictions.map((prediction) => {
          const outcome = outcomeOf(prediction, now);
          return (
            <li
              key={prediction.id}
              className="grid grid-cols-1 gap-4 py-3 first:pt-0 last:pb-0 lg:grid-cols-[minmax(12rem,1.2fr)_1fr_1fr]"
            >
              <div className="flex min-w-0 flex-col gap-0.5">
                <button
                  type="button"
                  className="group flex items-center gap-1 text-left text-sm font-semibold text-blue-700 underline-offset-2 hover:underline"
                  onClick={() => void navigate(`/sub-clients/${prediction.subClient.id}`)}
                >
                  {prediction.subClient.name}
                  <IconChevronRight
                    size={14}
                    className="transition-transform group-hover:translate-x-0.5"
                  />
                </button>
                <span className="text-xs text-gray-500">{prediction.subClient.address}</span>
                <Badge
                  size="sm"
                  variant="light"
                  color={OUTCOME_COLOR[outcome]}
                  className="mt-1 w-fit normal-case"
                >
                  {t(`predictedDeliveries.outcome.${outcome}`, {
                    count: lateDays(prediction, now),
                  })}
                </Badge>
              </div>

              <div className="flex flex-col gap-1">
                <span className="text-xs text-gray-500">
                  {t("predictedDeliveries.shipment.happened.predicted", {
                    date: predictedFor(prediction),
                  })}
                </span>
                {prediction.contentUnknown ? (
                  <span className="text-sm text-gray-500 italic">
                    {t("predictedDeliveries.shipment.happened.unknownLoad")}
                  </span>
                ) : (
                  loadList(prediction.lines)
                )}
              </div>

              <div className="flex flex-col gap-1">
                <span className="text-xs text-gray-500">
                  {prediction.actualVisitAt
                    ? t("predictedDeliveries.shipment.happened.actual", {
                        date: formatDate(prediction.actualVisitAt, client),
                      })
                    : t("predictedDeliveries.shipment.happened.noActual")}
                </span>
                {prediction.actualLines && prediction.actualLines.length > 0 ? (
                  loadList(prediction.actualLines)
                ) : (
                  <span className="text-sm text-gray-400">--</span>
                )}
              </div>
            </li>
          );
        })}
      </ul>
      {/* The verdict given on the shipment as a whole. */}
      <div className="flex items-center gap-2 border-t border-gray-100 pt-3 text-sm text-gray-700">
        <span className="text-xs text-gray-500">
          {t("predictedDeliveries.shipment.happened.feedback")}
        </span>
        {card.shipment.feedbackUseful === null ? (
          <span className="text-gray-400">--</span>
        ) : (
          <span className="flex items-center gap-1">
            {card.shipment.feedbackUseful ? (
              <IconThumbUp size={16} className="text-green-600" />
            ) : (
              <IconThumbDown size={16} className="text-red-600" />
            )}
            {card.shipment.feedbackReason &&
              t(`predictedDeliveries.feedback.reasons.${card.shipment.feedbackReason}`)}
            {card.shipment.feedbackComment && (
              <span className="text-gray-500 italic">· {card.shipment.feedbackComment}</span>
            )}
          </span>
        )}
      </div>
    </section>
  );
};

export default ShipmentOutcomeCard;
